import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyEmail } from '../api/auth';
import { useAuth } from '../auth/useAuth';
import { Alert, LoadingState } from '../components/ui';
import { AuthLayout } from './AuthLayout';

type Status = 'verifying' | 'verified' | 'failed' | 'missing-token';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { account, refresh } = useAuth();

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missing-token');
  // React 19 runs effects twice in development; a token is single-use, so the
  // second run would report a perfectly good link as invalid.
  const redeemed = useRef(false);

  useEffect(() => {
    if (!token || redeemed.current) {
      return;
    }
    redeemed.current = true;

    verifyEmail(token)
      .then(async () => {
        setStatus('verified');
        if (account) {
          await refresh();
        }
      })
      .catch(() => setStatus('failed'));
  }, [token, account, refresh]);

  return (
    <AuthLayout title="Confirming your email">
      {status === 'verifying' && <LoadingState label="Confirming your address…" />}

      {status === 'verified' && (
        <Alert tone="success" title="Address confirmed">
          Your email address is confirmed. <Link to="/">Start brewing</Link>.
        </Alert>
      )}

      {status === 'failed' && (
        <Alert tone="danger" title="This link did not work">
          It may have expired or already been used. You can ask for a new one from your account page
          after signing in.
        </Alert>
      )}

      {status === 'missing-token' && (
        <Alert tone="danger" title="Missing token">
          This confirmation link is incomplete. Please open the link from your email exactly as it
          was sent.
        </Alert>
      )}
    </AuthLayout>
  );
}
